/**
 * 汎用フォームバリデーション関数
 * 各画面で簡単に使用できる統一されたバリデーション処理を提供
 */

// エラー状態を保持するグローバル変数
let validationErrorState = new Set();

/**
 * エラーフィールドをクリアする
 * @param {Object} options - オプション
 */
export const clearValidationErrors = (options = {}) => {
  const {
    errorClass = "validation-error",
    labelClass = "validation-error-label",
    wrapperClass = "form-field-wrapper",
  } = options;

  validationErrorState.clear();
  document.querySelectorAll(`.${errorClass}`).forEach((element) => {
    element.classList.remove(errorClass);
  });
  document.querySelectorAll(`.${labelClass}`).forEach((label) => {
    label.remove();
  });
  document.querySelectorAll(`.${wrapperClass}`).forEach((wrapper) => {
    const parent = wrapper.parentNode;
    const child = wrapper.firstChild;
    if (child) {
      parent.insertBefore(child, wrapper);
    }
    wrapper.remove();
  });
};

/**
 * エラーフィールドを強調表示
 * @param {Array} errorFields - エラーフィールド名の配列
 * @param {Object} options - オプション
 */
export const highlightErrorFields = (errorFields, options = {}) => {
  const {
    errorClass = "validation-error",
    labelClass = "validation-error-label",
    wrapperClass = "form-field-wrapper",
    messageTemplate = "{label}は必須です",
    scrollToFirst = true,
    scrollOptions = {
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    },
    getCustomMessage = null,
    fieldLabels = {},
  } = options;

  clearValidationErrors({ errorClass, labelClass, wrapperClass });

  let firstErrorElement = null;

  errorFields.forEach((fieldName) => {
    const element =
      document.querySelector(`[name="${fieldName}"]`) ||
      document.querySelector(`[data-field="${fieldName}"]`) ||
      document.querySelector(`[data-name="${fieldName}"]`);

    if (element) {
      const mediaSelector = element.closest(".media-selector");
      if (mediaSelector) {
        const fileElement = mediaSelector.querySelector(".file.img");
        if (fileElement) {
          fileElement.classList.add(errorClass);
        }
      } else {
        element.classList.add(errorClass);
      }

      const label =
        element.getAttribute("data-label") ||
        fieldLabels[fieldName] ||
        fieldName;
      let errorMessage;

      if (getCustomMessage && typeof getCustomMessage === "function") {
        errorMessage = getCustomMessage(fieldName, label, element);
      } else {
        errorMessage = messageTemplate.replace("{label}", label);
      }

      const errorLabel = document.createElement("span");
      errorLabel.className = labelClass;
      errorLabel.textContent = errorMessage;
      errorLabel.style.cssText = `
        color: #ff6b6b !important;
        font-size: 0.8rem !important;
        display: block !important;
        position: relative !important;
        z-index: 10 !important;
        margin-top: 2px !important;
        animation: fadeInError 0.3s ease-in-out;
      `;

      if (!element.parentNode.classList.contains(wrapperClass)) {
        const wrapper = document.createElement("div");
        wrapper.className = wrapperClass;
        wrapper.style.cssText = `
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          width: 100% !important;
          position: relative !important;
        `;

        const parent = element.parentNode;
        const nextSibling = element.nextSibling;

        if (nextSibling) {
          parent.insertBefore(wrapper, nextSibling);
        } else {
          parent.appendChild(wrapper);
        }

        wrapper.appendChild(element);
      }

      const wrapper = element.parentNode;
      wrapper.appendChild(errorLabel);

      if (!firstErrorElement) {
        firstErrorElement = element;
      }
    }
  });

  if (scrollToFirst && firstErrorElement) {
    setTimeout(() => {
      firstErrorElement.scrollIntoView(scrollOptions);
    }, 100);
  }
};

/**
 * バリデーションルールに基づいてフィールドをチェック
 * @param {Object} data - チェックするデータオブジェクト
 * @param {Object} rules - バリデーションルール
 * @returns {Array} エラーフィールド名の配列
 */
export const validateFields = (data, rules) => {
  const errorFields = [];

  Object.entries(rules).forEach(([fieldName, rule]) => {
    const value = data[fieldName];

    if (rule.required) {
      if (
        !value ||
        (typeof value === "string" && !value.trim()) ||
        (typeof value === "object" && !value.id && !value.image_url)
      ) {
        errorFields.push(fieldName);
      }
    }

    if (rule.validator && typeof rule.validator === "function") {
      if (!rule.validator(value, data)) {
        errorFields.push(fieldName);
      }
    }

    if (rule.minLength && value && value.length < rule.minLength) {
      errorFields.push(fieldName);
    }

    if (rule.maxLength && value && value.length > rule.maxLength) {
      errorFields.push(fieldName);
    }

    if (rule.pattern && value && !rule.pattern.test(value)) {
      errorFields.push(fieldName);
    }
  });

  return errorFields;
};

/**
 * 入力フィールドのエラーをクリアする（個別）
 * @param {HTMLElement} element - 対象の要素
 * @param {Object} options - オプション
 */
export const clearFieldError = (element, options = {}) => {
  const {
    errorClass = "validation-error",
    labelClass = "validation-error-label",
    wrapperClass = "form-field-wrapper",
  } = options;

  if (element) {
    element.classList.remove(errorClass);

    const mediaSelector = element.closest(".media-selector");
    if (mediaSelector) {
      const fileElement = mediaSelector.querySelector(".file.img");
      if (fileElement) {
        fileElement.classList.remove(errorClass);
      }
    }

    const wrapper = element.closest(`.${wrapperClass}`);
    if (wrapper) {
      const errorLabel = wrapper.querySelector(`.${labelClass}`);
      if (errorLabel) {
        errorLabel.remove();
      }
    }
  }
};

/**
 * 汎用バリデーション実行関数
 * @param {Object} formData - バリデーション対象のフォームデータ
 * @param {Object} validationRules - バリデーションルール
 * @param {Object} fieldLabels - フィールド名と日本語ラベルのマッピング
 * @param {Function} setMessage - エラーメッセージ設定関数
 * @param {string} errorMessage - エラーメッセージ（デフォルト: '入力に不備があります。赤くハイライトされた項目を確認してください。'）
 * @returns {boolean} バリデーション結果（true: 成功, false: 失敗）
 */
export const executeValidation = (
  formData,
  validationRules,
  fieldLabels,
  setMessage,
  errorMessage = "入力に不備があります。赤くハイライトされた項目を確認してください。"
) => {
  // 既存のバリデーションエラーをクリア
  clearValidationErrors();

  // バリデーション実行
  const errorFields = validateFields(formData, validationRules);

  if (errorFields.length > 0) {
    // エラーフィールドをハイライト
    highlightErrorFields(errorFields, { fieldLabels });

    // エラーメッセージを表示
    setMessage(errorMessage);
    setTimeout(() => setMessage(""), 5000);

    return false;
  }

  return true;
};

/**
 * 入力変更時のエラークリア処理
 * @param {Event} event - 入力イベント
 * @param {Function} setPageContent - ページコンテンツ設定関数
 * @param {Function} handleInputChangeWithScroll - スクロール付き入力変更処理関数（オプション）
 * @param {Object} scrollOptions - スクロール処理のオプション（オプション）
 */
export const handleInputChangeWithValidation = (
  event,
  setPageContent,
  handleInputChangeWithScroll = null,
  scrollOptions = {}
) => {
  const { name, value } = event.target;

  // エラークラスをクリア
  clearFieldError(event.target);

  // ページコンテンツを更新
  setPageContent((prev) => ({
    ...prev,
    [name]: value,
  }));

  // スクロール処理が指定されている場合は実行
  if (handleInputChangeWithScroll) {
    handleInputChangeWithScroll(event, scrollOptions);
  }
};

/**
 * メディア選択時のエラークリア処理
 * @param {string} fieldName - フィールド名
 * @param {Object} media - 選択されたメディア
 * @param {Function} setPageContent - ページコンテンツ設定関数
 */
export const handleMediaSelectWithValidation = (
  fieldName,
  media,
  setPageContent
) => {
  // MediaSelector選択時もエラークラスをクリア
  const element = document.querySelector(`[data-field="${fieldName}"]`);
  if (element) {
    clearFieldError(element);
  }

  // ページコンテンツを更新
  setPageContent((prev) => ({
    ...prev,
    [fieldName]: media,
  }));
};

/**
 * リセット時のエラークリア処理
 * @param {Function} fetchPageContentData - ページデータ取得関数
 */
export const handleResetWithValidation = (fetchPageContentData) => {
  // エラーメッセージをクリア
  clearValidationErrors();

  // ページデータを再取得
  fetchPageContentData();
};

/**
 * 配列要素を含む汎用バリデーション実行関数
 * @param {Object} formData - バリデーション対象のフォームデータ
 * @param {Object} validationRules - 通常フィールドのバリデーションルール
 * @param {Object} arrayValidationRules - 配列フィールドのバリデーションルール
 * @param {Object} fieldLabels - フィールド名と日本語ラベルのマッピング
 * @param {Function} setMessage - エラーメッセージ設定関数
 * @param {string} errorMessage - エラーメッセージ
 * @returns {boolean} バリデーション結果（true: 成功, false: 失敗）
 */
export const executeValidationWithArrays = (
  formData,
  validationRules,
  arrayValidationRules,
  fieldLabels,
  setMessage,
  errorMessage = "入力に不備があります。赤くハイライトされた項目を確認してください。"
) => {
  // 既存のバリデーションエラーをクリア
  clearValidationErrors();

  // 通常フィールドのバリデーション
  const errorFields = validateFields(formData, validationRules);

  // 配列フィールドのバリデーション（HTML要素のname属性に合わせた形式）
  let arrayErrors = [];
  if (arrayValidationRules) {
    Object.entries(arrayValidationRules).forEach(([arrayFieldName, rules]) => {
      const arrayData = formData[arrayFieldName];
      if (Array.isArray(arrayData)) {
        const singularName = arrayFieldName.slice(0, -1); // 末尾の's'を除去
        arrayData.forEach((item, index) => {
          Object.entries(rules).forEach(([fieldName, rule]) => {
            const value = item[fieldName];
            // HTML要素のname属性に合わせた形式（about_item0_title）
            const errorFieldName = `${singularName}${index}_${fieldName}`;

            // 必須チェック
            if (rule.required) {
              if (
                !value ||
                (typeof value === "string" && !value.trim()) ||
                (typeof value === "object" && !value.id && !value.image_url)
              ) {
                arrayErrors.push(errorFieldName);
              }
            }

            // カスタムバリデーション
            if (rule.validator && typeof rule.validator === "function") {
              if (!rule.validator(value, item, index)) {
                arrayErrors.push(errorFieldName);
              }
            }

            // 最小長チェック
            if (rule.minLength && value && value.length < rule.minLength) {
              arrayErrors.push(errorFieldName);
            }

            // 最大長チェック
            if (rule.maxLength && value && value.length > rule.maxLength) {
              arrayErrors.push(errorFieldName);
            }

            // 正規表現チェック
            if (rule.pattern && value && !rule.pattern.test(value)) {
              arrayErrors.push(errorFieldName);
            }
          });
        });
      }
    });
  }

  // エラーフィールドを結合
  const allErrors = [...errorFields, ...arrayErrors];

  if (allErrors.length > 0) {
    // 配列要素のラベルを動的に追加
    const dynamicLabels = { ...fieldLabels };
    if (arrayValidationRules) {
      Object.entries(arrayValidationRules).forEach(
        ([arrayFieldName, rules]) => {
          const arrayData = formData[arrayFieldName];
          if (Array.isArray(arrayData)) {
            const singularName = arrayFieldName.slice(0, -1); // 末尾の's'を除去
            arrayData.forEach((_, index) => {
              Object.keys(rules).forEach((fieldName) => {
                const errorFieldName = `${singularName}${index}_${fieldName}`;
                if (!dynamicLabels[errorFieldName]) {
                  dynamicLabels[errorFieldName] = `項目${index + 1}の${
                    fieldName === "title"
                      ? "タイトル"
                      : fieldName === "text"
                      ? "内容"
                      : fieldName
                  }`;
                }
              });
            });
          }
        }
      );
    }

    // エラーフィールドをハイライト
    highlightErrorFields(allErrors, { fieldLabels: dynamicLabels });

    // エラーメッセージを表示
    setMessage(errorMessage);
    setTimeout(() => setMessage(""), 5000);

    return false;
  }

  return true;
};

/**
 * 配列要素の入力変更時のエラークリア処理
 * @param {Event} event - 入力イベント
 * @param {number} index - 配列インデックス
 * @param {string} fieldName - フィールド名
 * @param {Function} setPageContent - ページコンテンツ設定関数
 * @param {string} arrayFieldName - 配列フィールド名
 * @param {Function} iframeCallback - iframe通信処理関数（オプション）
 */
export const handleArrayInputChangeWithValidation = (
  event,
  index,
  fieldName,
  setPageContent,
  arrayFieldName,
  iframeCallback = null
) => {
  const { value } = event.target;

  // エラークラスをクリア
  clearFieldError(event.target);

  // ページコンテンツを更新
  setPageContent((prev) => {
    const arrayData = prev[arrayFieldName].map((item, i) =>
      i === index ? { ...item, [fieldName]: value } : item
    );
    const newData = { ...prev, [arrayFieldName]: arrayData };

    // iframe通信処理が指定されている場合は実行
    if (iframeCallback) {
      iframeCallback(newData, index, fieldName, value, event.type);
    }

    return newData;
  });
};

/**
 * 配列要素のメディア選択時のエラークリア処理
 * @param {number} index - 配列インデックス
 * @param {string} fieldName - フィールド名
 * @param {Object} media - 選択されたメディア
 * @param {Function} setPageContent - ページコンテンツ設定関数
 * @param {string} arrayFieldName - 配列フィールド名
 * @param {Function} iframeCallback - iframe通信処理関数（オプション）
 */
export const handleArrayMediaSelectWithValidation = (
  index,
  fieldName,
  media,
  setPageContent,
  arrayFieldName,
  iframeCallback = null
) => {
  // ページコンテンツを更新
  setPageContent((prev) => {
    const arrayData = prev[arrayFieldName].map((item, i) =>
      i === index ? { ...item, [fieldName]: media } : item
    );
    const newData = { ...prev, [arrayFieldName]: arrayData };

    // iframe通信処理が指定されている場合は実行
    if (iframeCallback) {
      iframeCallback(newData, index, fieldName, media);
    }

    return newData;
  });
};

/**
 * 配列要素削除時のエラー状態再評価処理
 * @param {number} index - 削除するインデックス
 * @param {Function} setPageContent - ページコンテンツ設定関数
 * @param {string} arrayFieldName - 配列フィールド名
 * @param {Object} arrayValidationRules - 配列バリデーションルール
 * @param {Object} fieldLabels - フィールドラベル
 * @param {Function} iframeCallback - iframe通信処理関数（オプション）
 */
export const handleArrayItemRemoveWithValidation = (
  index,
  setPageContent,
  arrayFieldName,
  arrayValidationRules = null,
  fieldLabels = {},
  iframeCallback = null
) => {
  setPageContent((prev) => {
    const newData = {
      ...prev,
      [arrayFieldName]: prev[arrayFieldName].filter((_, i) => i !== index),
    };

    // iframe通信処理が指定されている場合は実行
    if (iframeCallback) {
      iframeCallback(newData);
    }

    return newData;
  });

  // 削除後にエラー状態を再評価
  setTimeout(() => {
    if (arrayValidationRules) {
      // 既存のエラーをクリア
      clearValidationErrors();

      // 削除後のデータで再バリデーション
      setPageContent((currentData) => {
        const singularName = arrayFieldName.slice(0, -1);
        const arrayData = currentData[arrayFieldName];
        let arrayErrors = [];

        if (Array.isArray(arrayData)) {
          arrayData.forEach((item, newIndex) => {
            Object.entries(arrayValidationRules).forEach(
              ([fieldName, rule]) => {
                const value = item[fieldName];
                const errorFieldName = `${singularName}${newIndex}_${fieldName}`;

                // 必須チェック
                if (rule.required) {
                  if (
                    !value ||
                    (typeof value === "string" && !value.trim()) ||
                    (typeof value === "object" && !value.id && !value.image_url)
                  ) {
                    arrayErrors.push(errorFieldName);
                  }
                }

                // その他のバリデーション
                if (rule.validator && typeof rule.validator === "function") {
                  if (!rule.validator(value, item, newIndex)) {
                    arrayErrors.push(errorFieldName);
                  }
                }

                if (rule.minLength && value && value.length < rule.minLength) {
                  arrayErrors.push(errorFieldName);
                }

                if (rule.maxLength && value && value.length > rule.maxLength) {
                  arrayErrors.push(errorFieldName);
                }

                if (rule.pattern && value && !rule.pattern.test(value)) {
                  arrayErrors.push(errorFieldName);
                }
              }
            );
          });
        }

        // エラーがある場合は再表示
        if (arrayErrors.length > 0) {
          const dynamicLabels = { ...fieldLabels };

          arrayData.forEach((_, newIndex) => {
            Object.keys(arrayValidationRules).forEach((fieldName) => {
              const errorFieldName = `${singularName}${newIndex}_${fieldName}`;
              if (!dynamicLabels[errorFieldName]) {
                dynamicLabels[errorFieldName] = `項目${newIndex + 1}の${
                  fieldName === "title"
                    ? "タイトル"
                    : fieldName === "text"
                    ? "内容"
                    : fieldName
                }`;
              }
            });
          });

          highlightErrorFields(arrayErrors, { fieldLabels: dynamicLabels });
        }

        return currentData;
      });
    } else {
      // バリデーションルールが指定されていない場合は全クリア
      clearValidationErrors();
    }
  }, 100);
};

/**
 * ネストした配列要素を含む汎用バリデーション実行関数
 * @param {Object} formData - バリデーション対象のフォームデータ
 * @param {Object} validationRules - 通常フィールドのバリデーションルール
 * @param {Object} nestedArrayValidationRules - ネスト配列フィールドのバリデーションルール
 * @param {Object} fieldLabels - フィールド名と日本語ラベルのマッピング
 * @param {Function} setMessage - エラーメッセージ設定関数
 * @param {string} errorMessage - エラーメッセージ
 * @returns {boolean} バリデーション結果（true: 成功, false: 失敗）
 */
export const executeValidationWithNestedArrays = (
  formData,
  validationRules,
  nestedArrayValidationRules,
  fieldLabels,
  setMessage,
  errorMessage = "入力に不備があります。赤くハイライトされた項目を確認してください。"
) => {
  // 既存のバリデーションエラーをクリア
  clearValidationErrors();

  // 通常フィールドのバリデーション
  const errorFields = validateFields(formData, validationRules);

  // ネストした配列フィールドのバリデーション
  let nestedArrayErrors = [];
  if (nestedArrayValidationRules) {
    Object.entries(nestedArrayValidationRules).forEach(
      ([arrayFieldName, config]) => {
        const arrayData = formData[arrayFieldName];
        if (Array.isArray(arrayData)) {
          const singularName = arrayFieldName.slice(0, -1); // 末尾の's'を除去

          arrayData.forEach((item, index) => {
            // 第1レベル配列の検証
            if (config.firstLevel) {
              Object.entries(config.firstLevel).forEach(([fieldName, rule]) => {
                const value = item[fieldName];
                const errorFieldName = `${singularName}${index}_${fieldName}`;

                if (rule.required) {
                  if (
                    !value ||
                    (typeof value === "string" && !value.trim()) ||
                    (typeof value === "object" && !value.id && !value.image_url)
                  ) {
                    nestedArrayErrors.push(errorFieldName);
                  }
                }

                // その他のバリデーション
                if (rule.validator && typeof rule.validator === "function") {
                  if (!rule.validator(value, item, index)) {
                    nestedArrayErrors.push(errorFieldName);
                  }
                }

                if (rule.minLength && value && value.length < rule.minLength) {
                  nestedArrayErrors.push(errorFieldName);
                }

                if (rule.maxLength && value && value.length > rule.maxLength) {
                  nestedArrayErrors.push(errorFieldName);
                }

                if (rule.pattern && value && !rule.pattern.test(value)) {
                  nestedArrayErrors.push(errorFieldName);
                }
              });
            }

            // 第2レベル配列の検証
            if (config.secondLevel && config.secondLevelField) {
              const secondLevelArray = item[config.secondLevelField];
              if (Array.isArray(secondLevelArray)) {
                secondLevelArray.forEach((secondItem, secondIndex) => {
                  Object.entries(config.secondLevel).forEach(
                    ([fieldName, rule]) => {
                      const value = secondItem[fieldName];
                      const errorFieldName = `${singularName}${index}_${secondIndex}_${fieldName}`;

                      if (rule.required) {
                        if (
                          !value ||
                          (typeof value === "string" && !value.trim()) ||
                          (typeof value === "object" &&
                            !value.id &&
                            !value.image_url)
                        ) {
                          nestedArrayErrors.push(errorFieldName);
                        }
                      }

                      // その他のバリデーション
                      if (
                        rule.validator &&
                        typeof rule.validator === "function"
                      ) {
                        if (
                          !rule.validator(
                            value,
                            secondItem,
                            secondIndex,
                            item,
                            index
                          )
                        ) {
                          nestedArrayErrors.push(errorFieldName);
                        }
                      }

                      if (
                        rule.minLength &&
                        value &&
                        value.length < rule.minLength
                      ) {
                        nestedArrayErrors.push(errorFieldName);
                      }

                      if (
                        rule.maxLength &&
                        value &&
                        value.length > rule.maxLength
                      ) {
                        nestedArrayErrors.push(errorFieldName);
                      }

                      if (rule.pattern && value && !rule.pattern.test(value)) {
                        nestedArrayErrors.push(errorFieldName);
                      }
                    }
                  );
                });
              }
            }
          });
        }
      }
    );
  }

  // エラーフィールドを結合
  const allErrors = [...errorFields, ...nestedArrayErrors];

  if (allErrors.length > 0) {
    // ネスト配列要素のラベルを動的に追加
    const dynamicLabels = { ...fieldLabels };
    if (nestedArrayValidationRules) {
      Object.entries(nestedArrayValidationRules).forEach(
        ([arrayFieldName, config]) => {
          const arrayData = formData[arrayFieldName];
          if (Array.isArray(arrayData)) {
            const singularName = arrayFieldName.slice(0, -1);

            arrayData.forEach((item, index) => {
              // 第1レベル配列のラベル
              if (config.firstLevel) {
                Object.keys(config.firstLevel).forEach((fieldName) => {
                  const errorFieldName = `${singularName}${index}_${fieldName}`;
                  if (!dynamicLabels[errorFieldName]) {
                    dynamicLabels[errorFieldName] = `セクション${index + 1}の${
                      fieldName === "section_title" ? "タイトル" : fieldName
                    }`;
                  }
                });
              }

              // 第2レベル配列のラベル
              if (config.secondLevel && config.secondLevelField) {
                const secondLevelArray = item[config.secondLevelField];
                if (Array.isArray(secondLevelArray)) {
                  secondLevelArray.forEach((_, secondIndex) => {
                    Object.keys(config.secondLevel).forEach((fieldName) => {
                      const errorFieldName = `${singularName}${index}_${secondIndex}_${fieldName}`;
                      if (!dynamicLabels[errorFieldName]) {
                        dynamicLabels[errorFieldName] = `セクション${
                          index + 1
                        }の項目${secondIndex + 1}の${
                          fieldName === "title"
                            ? "タイトル"
                            : fieldName === "price"
                            ? "価格"
                            : fieldName
                        }`;
                      }
                    });
                  });
                }
              }
            });
          }
        }
      );
    }

    // エラーフィールドをハイライト
    highlightErrorFields(allErrors, { fieldLabels: dynamicLabels });

    // エラーメッセージを表示
    setMessage(errorMessage);
    setTimeout(() => setMessage(""), 5000);

    return false;
  }

  return true;
};

/**
 * ネスト配列要素の入力変更時のエラークリア処理
 * @param {Event} event - 入力イベント
 * @param {number} firstIndex - 第1レベルインデックス
 * @param {string} fieldName - フィールド名
 * @param {Function} setPageContent - ページコンテンツ設定関数
 * @param {string} arrayFieldName - 配列フィールド名
 * @param {number} secondIndex - 第2レベルインデックス（オプション）
 * @param {string} secondLevelField - 第2レベルフィールド名（オプション）
 * @param {Function} iframeCallback - iframe通信処理関数（オプション）
 */
export const handleNestedArrayInputChangeWithValidation = (
  event,
  firstIndex,
  fieldName,
  setPageContent,
  arrayFieldName,
  secondIndex = null,
  secondLevelField = null,
  iframeCallback = null
) => {
  const { value } = event.target;

  // エラークラスをクリア
  clearFieldError(event.target);

  // ページコンテンツを更新
  setPageContent((prev) => {
    let newData;

    if (secondIndex !== null && secondLevelField) {
      // ネスト配列の場合
      newData = {
        ...prev,
        [arrayFieldName]: prev[arrayFieldName].map((item, i) => {
          if (i === firstIndex) {
            const updatedSecondLevel = [...(item[secondLevelField] || [])];
            if (!updatedSecondLevel[secondIndex]) {
              updatedSecondLevel[secondIndex] = {};
            }
            updatedSecondLevel[secondIndex] = {
              ...updatedSecondLevel[secondIndex],
              [fieldName]: value,
            };
            return {
              ...item,
              [secondLevelField]: updatedSecondLevel,
            };
          }
          return item;
        }),
      };
    } else {
      // 第1レベル配列の場合
      newData = {
        ...prev,
        [arrayFieldName]: prev[arrayFieldName].map((item, i) =>
          i === firstIndex ? { ...item, [fieldName]: value } : item
        ),
      };
    }

    // iframe通信処理が指定されている場合は実行
    if (iframeCallback) {
      iframeCallback(
        newData,
        firstIndex,
        fieldName,
        value,
        event.type,
        secondIndex
      );
    }

    return newData;
  });
};

/**
 * PriceEditPage専用：ネスト配列要素削除時のエラー状態再評価処理
 * HTML要素のname属性動的変更に対応
 * @param {number} firstIndex - 第1レベルインデックス
 * @param {Function} setPageContent - ページコンテンツ設定関数
 * @param {string} arrayFieldName - 配列フィールド名
 * @param {Object} nestedArrayValidationRules - ネスト配列バリデーションルール
 * @param {Object} fieldLabels - フィールドラベル
 * @param {number} secondIndex - 第2レベルインデックス（オプション）
 * @param {string} secondLevelField - 第2レベルフィールド名（オプション）
 * @param {Function} iframeCallback - iframe通信処理関数（オプション）
 */
export const handlePriceEditPageItemRemoveWithValidation = (
  firstIndex,
  setPageContent,
  arrayFieldName,
  nestedArrayValidationRules = null,
  fieldLabels = {},
  secondIndex = null,
  secondLevelField = null,
  iframeCallback = null
) => {
  setPageContent((prev) => {
    let newData;

    if (secondIndex !== null && secondLevelField) {
      // ネスト配列内の要素削除
      newData = {
        ...prev,
        [arrayFieldName]: prev[arrayFieldName].map((item, i) => {
          if (i === firstIndex) {
            const updatedSecondLevel = [...(item[secondLevelField] || [])];
            updatedSecondLevel.splice(secondIndex, 1);
            return {
              ...item,
              [secondLevelField]: updatedSecondLevel,
            };
          }
          return item;
        }),
      };
    } else {
      // 第1レベル配列の要素削除
      newData = {
        ...prev,
        [arrayFieldName]: prev[arrayFieldName].filter(
          (_, i) => i !== firstIndex
        ),
      };
    }

    // iframe通信処理が指定されている場合は実行
    if (iframeCallback) {
      iframeCallback(newData, firstIndex, secondIndex);
    }

    return newData;
  });

  // 削除後にエラー状態を再評価（PriceEditPage専用）
  setTimeout(() => {
    if (nestedArrayValidationRules) {
      // 削除後のデータで即座に再バリデーション実行
      setPageContent((currentData) => {
        // 既存のエラーをクリア
        clearValidationErrors();
        const singularName = arrayFieldName.slice(0, -1);
        const arrayData = currentData[arrayFieldName];
        let nestedArrayErrors = [];

        if (Array.isArray(arrayData)) {
          // DOM要素のname属性を実際の構造に基づいて更新
          const allInputs = document.querySelectorAll('input[data-index]');
          allInputs.forEach(input => {
            const dataIndex = input.getAttribute('data-index');
            if (dataIndex && dataIndex.includes('-')) {
              const [itemIdx, priceIdx] = dataIndex.split('-').map(Number);
              if (itemIdx < arrayData.length && arrayData[itemIdx] && 
                  arrayData[itemIdx][secondLevelField] && 
                  priceIdx < arrayData[itemIdx][secondLevelField].length) {
                const fieldName = input.getAttribute('name');
                if (fieldName === 'title' || fieldName === 'price') {
                  input.setAttribute('name', `${singularName}${itemIdx}_${priceIdx}_${fieldName}`);
                }
              }
            }
          });

          // 再バリデーション実行
          Object.entries(nestedArrayValidationRules).forEach(([_, config]) => {
            arrayData.forEach((item, index) => {
              // 第1レベル配列の検証
              if (config.firstLevel) {
                Object.entries(config.firstLevel).forEach(
                  ([fieldName, rule]) => {
                    const value = item[fieldName];
                    const errorFieldName = `${singularName}${index}_${fieldName}`;

                    if (rule.required && (!value || value.trim() === "")) {
                      nestedArrayErrors.push(errorFieldName);
                    }
                  }
                );
              }

              // 第2レベル配列の検証
              if (config.secondLevel && config.secondLevelField) {
                const secondLevelArray = item[config.secondLevelField];
                if (Array.isArray(secondLevelArray)) {
                  secondLevelArray.forEach((secondItem, newSecondIndex) => {
                    Object.entries(config.secondLevel).forEach(
                      ([fieldName, rule]) => {
                        const value = secondItem[fieldName];
                        const errorFieldName = `${singularName}${index}_${newSecondIndex}_${fieldName}`;

                        if (rule.required && (!value || value.trim() === "")) {
                          nestedArrayErrors.push(errorFieldName);
                        }
                      }
                    );
                  });
                }
              }
            });
          });
        }

        // 即座に再バリデーション実行
        setTimeout(() => {
          if (nestedArrayErrors.length > 0) {
            const dynamicLabels = { ...fieldLabels };

            arrayData.forEach((item, index) => {
              if (nestedArrayValidationRules[arrayFieldName]?.firstLevel) {
                Object.keys(nestedArrayValidationRules[arrayFieldName].firstLevel).forEach((fieldName) => {
                  const errorFieldName = `${singularName}${index}_${fieldName}`;
                  if (!dynamicLabels[errorFieldName]) {
                    dynamicLabels[errorFieldName] = `セクション${index + 1}の${fieldName === "section_title" ? "タイトル" : fieldName}`;
                  }
                });
              }

              if (nestedArrayValidationRules[arrayFieldName]?.secondLevel && nestedArrayValidationRules[arrayFieldName]?.secondLevelField) {
                const secondLevelArray = item[nestedArrayValidationRules[arrayFieldName].secondLevelField];
                if (Array.isArray(secondLevelArray)) {
                  secondLevelArray.forEach((_, newSecondIndex) => {
                    Object.keys(nestedArrayValidationRules[arrayFieldName].secondLevel).forEach((fieldName) => {
                      const errorFieldName = `${singularName}${index}_${newSecondIndex}_${fieldName}`;
                      if (!dynamicLabels[errorFieldName]) {
                        dynamicLabels[errorFieldName] = `セクション${index + 1}の項目${newSecondIndex + 1}の${fieldName === "title" ? "タイトル" : fieldName === "price" ? "価格" : fieldName}`;
                      }
                    });
                  });
                }
              }
            });

            highlightErrorFields(nestedArrayErrors, { fieldLabels: dynamicLabels });
          }
        }, 50);

        return currentData;
      });
    } else {
      // バリデーションルールが指定されていない場合は全クリア
      clearValidationErrors();
    }
  }, 150); // PriceEditPageの既存処理に合わせて150ms
};

/**
 * 特定のフィールド名パターンに一致するエラーをクリアする
 * @param {Array} fieldPatterns - クリアするフィールド名のパターン配列
 * @param {Object} options - オプション
 */
export const clearSpecificFieldErrors = (fieldPatterns, options = {}) => {
  const {
    errorClass = "validation-error",
    labelClass = "validation-error-label",
    wrapperClass = "form-field-wrapper",
  } = options;

  fieldPatterns.forEach((pattern) => {
    const elements = document.querySelectorAll(
      `[name*="${pattern}"], [data-field*="${pattern}"], [data-name*="${pattern}"]`
    );

    elements.forEach((element) => {
      if (element.classList.contains(errorClass)) {
        element.classList.remove(errorClass);

        const mediaSelector = element.closest(".media-selector");
        if (mediaSelector) {
          mediaSelector.classList.remove(errorClass);
        }

        const wrapper = element.closest(`.${wrapperClass}`);
        if (wrapper) {
          const errorLabel = wrapper.querySelector(`.${labelClass}`);
          if (errorLabel) {
            errorLabel.remove();
          }
        }
      }
    });
  });
};

/**
 * 配列要素削除時のエラー状態調整（インデックス変更に対応）
 * @param {string} arrayPrefix - 配列フィールドのプレフィックス
 * @param {number} deletedIndex - 削除されたインデックス
 * @param {number} totalCount - 削除後の配列の総数
 * @param {Object} options - オプション
 */
export const adjustArrayErrorsAfterDeletion = (
  arrayPrefix,
  deletedIndex,
  totalCount,
  options = {}
) => {
  const {
    errorClass = "validation-error",
    labelClass = "validation-error-label",
    wrapperClass = "form-field-wrapper",
  } = options;

  const errorStates = [];

  for (let i = deletedIndex + 1; i < totalCount + 1; i++) {
    const elements = document.querySelectorAll(
      `[name*="${arrayPrefix}${i}_"], [name*="${arrayPrefix}[${i}]."]`
    );

    elements.forEach((element) => {
      if (element.classList.contains(errorClass)) {
        const fieldName =
          element.getAttribute("name") ||
          element.getAttribute("data-field") ||
          element.getAttribute("data-name");
        const wrapper = element.closest(`.${wrapperClass}`);
        const errorLabel = wrapper
          ? wrapper.querySelector(`.${labelClass}`)
          : null;

        errorStates.push({
          originalIndex: i,
          newIndex: i - 1,
          fieldName: fieldName,
          element: element,
          errorMessage: errorLabel ? errorLabel.textContent : null,
        });

        element.classList.remove(errorClass);
        if (errorLabel) {
          errorLabel.remove();
        }
      }
    });
  }

  const deletedElements = document.querySelectorAll(
    `[name*="${arrayPrefix}${deletedIndex}_"], [name*="${arrayPrefix}[${deletedIndex}]."]`
  );
  deletedElements.forEach((element) => {
    if (element.classList.contains(errorClass)) {
      element.classList.remove(errorClass);
      const wrapper = element.closest(`.${wrapperClass}`);
      if (wrapper) {
        const errorLabel = wrapper.querySelector(`.${labelClass}`);
        if (errorLabel) {
          errorLabel.remove();
        }
      }
    }
  });

  setTimeout(() => {
    errorStates.forEach((errorState) => {
      const newFieldName = errorState.fieldName.replace(
        new RegExp(`${arrayPrefix}${errorState.originalIndex}([_\\.])`),
        `${arrayPrefix}${errorState.newIndex}$1`
      );

      const newElement = document.querySelector(
        `[name="${newFieldName}"], [data-field="${newFieldName}"], [data-name="${newFieldName}"]`
      );

      if (newElement && errorState.errorMessage) {
        newElement.classList.add(errorClass);

        const wrapper =
          newElement.closest(`.${wrapperClass}`) || newElement.parentNode;
        if (wrapper && !wrapper.querySelector(`.${labelClass}`)) {
          const errorLabel = document.createElement("span");
          errorLabel.className = labelClass;
          errorLabel.textContent = errorState.errorMessage;
          errorLabel.style.cssText = `
            color: #ff6b6b !important;
            font-size: 0.8rem !important;
            display: block !important;
            position: relative !important;
            z-index: 10 !important;
            margin-top: 2px !important;
            animation: fadeInError 0.3s ease-in-out;
          `;

          if (wrapper.classList.contains(wrapperClass)) {
            wrapper.appendChild(errorLabel);
          } else {
            const wrapperDiv = document.createElement("div");
            wrapperDiv.className = wrapperClass;
            wrapperDiv.style.cssText = `
              display: flex !important;
              flex-direction: column !important;
              gap: 4px !important;
              width: 100% !important;
              position: relative !important;
            `;

            const parent = newElement.parentNode;
            const nextSibling = newElement.nextSibling;

            wrapperDiv.appendChild(newElement);
            wrapperDiv.appendChild(errorLabel);

            if (nextSibling) {
              parent.insertBefore(wrapperDiv, nextSibling);
            } else {
              parent.appendChild(wrapperDiv);
            }
          }
        }
      }
    });
  }, 0);
};

/**
 * AccessEditPage専用：配列要素削除時のエラー状態調整
 * @param {Object} pageContent - 現在のページコンテンツ（削除後の状態）
 * @param {number} studioIndex - スタジオのインデックス
 * @param {Object} options - オプション
 */
export const revalidateAccessPageAfterDeletion = (
  pageContent,
  studioIndex,
  options = {}
) => {
  const {
    errorClass = "validation-error",
    labelClass = "validation-error-label",
    wrapperClass = "form-field-wrapper",
  } = options;

  const informationElements = document.querySelectorAll(
    `[name^="info_"][name*="_${studioIndex}_"]`
  );
  informationElements.forEach((element) => {
    if (element.classList.contains(errorClass)) {
      element.classList.remove(errorClass);
      const wrapper = element.closest(`.${wrapperClass}`);
      if (wrapper) {
        const errorLabel = wrapper.querySelector(`.${labelClass}`);
        if (errorLabel) {
          errorLabel.remove();
        }
      }
    }
  });

  setTimeout(() => {
    const studio = pageContent.studios[studioIndex];
    if (studio && studio.information) {
      const errorFields = [];
      const fieldLabels = {};

      studio.information.forEach((info, infoIndex) => {
        if (!info.title || info.title.trim() === "") {
          const fieldName = `info_title_${studioIndex}_${infoIndex}`;
          errorFields.push(fieldName);
          fieldLabels[fieldName] = `スタジオ${studioIndex + 1}の情報${
            infoIndex + 1
          }のタイトル`;
        }
        if (!info.content || info.content.trim() === "") {
          const fieldName = `info_content_${studioIndex}_${infoIndex}`;
          errorFields.push(fieldName);
          fieldLabels[fieldName] = `スタジオ${studioIndex + 1}の情報${
            infoIndex + 1
          }の内容`;
        }
      });

      if (errorFields.length > 0) {
        highlightErrorFields(errorFields, { fieldLabels });
      }
    }
  }, 100);
};
